<template>
  <div v-show="deliverables.length > 0">
    <a
      :class="{
        error: deliverables.length > 0,
        blue: deliverables.length === 0,
      }"
      class="ui icon label circular margin-left-zero"
      @click="openModal"
    >
      <i
        class="icon"
        :class="{
          'exclamation triangle': deliverables.length > 0,
          'blue check circle': deliverables.length === 0,
        }"
      ></i>
      {{ deliverables.length }} Issue{{ deliverables.length === 1 ? "" : "s" }}
    </a>

    <div class="ui modal" ref="modal">
      <i class="icon close"></i>
      <div class="ui header">
        Assistant
        <div class="sub header">
          {{ deliverables.length | pluralize("Issue") }}
        </div>
      </div>
      <div class="content">
        <div
          class="ui yellow message"
          v-if="!hideDisconnectedAccounts && requiredProviders.size > 0"
        >
          <div class="ui center aligned small header">
            <strong>Connect these acccounts</strong>
          </div>

          <div class="ui list">
            <div
              v-for="provider in requiredProviders"
              class="item"
              :key="provider"
            >
              <a
                :href="`/authorize/${provider}?origin=${currentUrl}`"
                class="ui fluid white button bordered"
                data-turbo="false"
              >
                <i class="large icon colored" :class="provider"></i>
                <strong>Connect {{ provider | capitalize }}</strong>
              </a>
            </div>
          </div>
        </div>

        <div class="ui segment" v-show="deliverables.length < 1">
          No deliverables.
        </div>

        <div
          class="ui info message"
          v-if="neverAttempted.length == nonIgStoryDeliverables.length"
        >
          We're still fetching stats for these deliverables. Please check back
          soon.
        </div>

        <div v-show="deliverables.length > 0">
          <div
            v-if="!onlyErrored && igStoryDeliverables.length > 0"
            class="ui segment"
          >
            <i class="icon grey large instagram_stories"></i>
            <div class="content">
              Instagram Stories Frames ({{ igStoryDeliverables.length }})
            </div>
          </div>

          <div
            v-for="deliverable in deliverables"
            :key="deliverable.id"
            class="ui segment"
            :class="{
              errored: deliverable.reporting_status.status != 'ok',
            }"
          >
            <div
              class="ui top attached header"
              :style="{
                backgroundColor:
                  colors[inferredTypeAsIconClass(deliverable.inferred_type)] ||
                  '#cccccc',
              }"
            >
              <i
                :class="refreshIconFor(deliverable)"
                :data-tooltip="refreshTooltipFor(deliverable)"
                data-position="bottom right"
                data-variation="wide"
                class="link right floated"
                @click.alt="openReportingStatus(deliverable, $event)"
                @click.exact="refreshStats(deliverable)"
              />

              <i
                class="icon left floated"
                :class="inferredTypeAsIconClass(deliverable.inferred_type)"
              />
              {{ inferredTypeAsLabel(deliverable.inferred_type) | toCamelCase }}
            </div>

            <div class="content">
              <div class="ui message clearing">
                <span
                  v-html="statusMessage(deliverable)"
                ></span>

                <a
                  v-if="
                    deliverable.last_attempted_at &&
                    !deliverable.last_fetched &&
                    deliverable.reporting_status.status !== 'disconnected'
                  "
                  href="#"
                  @click.stop.prevent="refreshStats(deliverable)"
                  class="right floated"
                >
                  <i :class="refreshIconFor(deliverable)"></i>
                  {{ refreshLinkLabel(deliverable) }}
                </a>

                <div class="margin-top-small">
                  <span class="ui grey text deliverableLabel">
                    {{ deliverableLabel(deliverable) }}
                    <a
                      v-if="hasUrl(deliverable)"
                      :href="deliverable.url"
                      target="_blank"
                    >
                      <i class="icon external"></i>
                    </a>
                  </span>
                </div>

                <a
                  v-if="
                    ['unsupported', 'linkedin_personal_post'].includes(
                      deliverable.last_error_subtype
                    )
                  "
                  href="http://help.influencekit.com/en/articles/3468401-how-do-i-add-facebook-stories-reels-or-expired-instagram-stories-to-a-report"
                  class="ui pink tertiary button"
                  target="_blank"
                >
                  <i class="icon lightbulb"></i>
                  <strong>Here's how to add stats manually</strong>
                </a>
              </div>

              <!-- Per-deliverable chat (influencer side) -->
              <div class="margin-top-small" v-if="!chatUrl">
                <a
                  href="#"
                  class="kit-help-toggle"
                  @click.stop.prevent="toggleChat(deliverable.id)"
                >
                  <i class="icon magic"></i>
                  Ask Kit for help
                  <i
                    class="icon"
                    :class="
                      expandedChats[deliverable.id]
                        ? 'chevron up'
                        : 'chevron down'
                    "
                  ></i>
                </a>

                <div v-show="expandedChats[deliverable.id]" class="kit-chat-container">
                  <turbo-frame
                    v-if="loadedChats[deliverable.id]"
                    :id="`deliverable-troubleshoot-chat-${deliverable.id}`"
                    :src="`/deliverables/${deliverable.id}/troubleshoot`"
                    loading="lazy"
                  >
                    <div class="ui active centered inline loader small margin-top-small"></div>
                  </turbo-frame>
                </div>
              </div>

              <p class="flex flex-justify-end" v-if="!chatUrl">
                <a
                  href="mailto:help@influencekit.com"
                  class="show-intercom ui tertiary button icon flex-shrink-0"
                  :data-message="`I need help troubleshooting a deliverable: ${
                    deliverable.url
                  }\n\n(${deliverable.id}) Error: ${statusMessage(
                    deliverable
                  )}`"
                >
                  <i class="icon help"></i>
                  Get more help
                </a>
              </p>
            </div>
          </div>
        </div>

        <!-- Assignment-level chat (brand side) -->
        <div v-if="chatUrl" class="margin-top-medium">
          <a
            href="#"
            class="kit-help-toggle"
            @click.stop.prevent="toggleChat('assignment')"
          >
            <i class="icon magic"></i>
            Ask Kit for help
            <i
              class="icon"
              :class="expandedChats['assignment'] ? 'chevron up' : 'chevron down'"
            ></i>
          </a>

          <div v-show="expandedChats['assignment']" class="kit-chat-container">
            <turbo-frame
              v-if="loadedChats['assignment']"
              id="brand_chat"
              :src="chatUrl"
              loading="lazy"
            >
              <div class="ui active centered inline loader small margin-top-small"></div>
            </turbo-frame>
          </div>

          <p class="flex flex-justify-end margin-top-small">
            <a
              href="mailto:help@influencekit.com"
              class="show-intercom ui tertiary button icon flex-shrink-0"
              :data-message="allDeliverableErrors"
            >
              <i class="icon help"></i>
              Get more help
            </a>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import store from "store";
import { mapState, mapGetters } from "vuex";
import { isEmpty } from "lodash";
import {
  REFRESH_STATES,
  createRefreshTracker,
  refreshIconClass,
  refreshTitle,
  relativeUpdatedLabel,
} from "lib/refreshStatsState.mjs";

const { $ } = window;

export default {
  store,
  name: "Troubleshooter",
  props: {
    hideManageLink: {
      default: true,
      type: Boolean,
    },
    currentUrl: {
      type: String,
      default: window.location.href,
    },
    initialDeliverables: Array,
    chatUrl: {
      type: String,
      default: "",
    },
    hideDisconnectedAccounts: {
      type: Boolean,
      default: false,
    },
    onlyErrored: {
      default: true,
      type: Boolean,
    },
  },
  data() {
    return {
      expandedChats: {},
      loadedChats: {},
      // deliverable id -> refresh state. The trackers themselves live off the
      // reactive tree in created(), because they own timers.
      refreshStates: {},
      refreshClock: Date.now(),
    };
  },
  methods: {
    toggleChat(deliverableId) {
      const isExpanded = !!this.expandedChats[deliverableId];
      this.$set(this.expandedChats, deliverableId, !isExpanded);
      if (!this.loadedChats[deliverableId]) {
        this.$set(this.loadedChats, deliverableId, true);
      }
      this.$nextTick(() => this.refreshModal());
    },
    // This used to reach into the DOM and set a `loading` class that nothing
    // ever removed — the troubleshooter, of all screens, told you a refresh was
    // in progress forever. It now runs the same state machine as the report
    // cards, including the completion broadcast and the fallback.
    refreshTrackerFor(deliverable) {
      if (!this.refreshTrackers[deliverable.id]) {
        this.refreshTrackers[deliverable.id] = createRefreshTracker({
          onChange: (state) =>
            this.$set(this.refreshStates, deliverable.id, state),
          onTimeout: () => this.recheckRefresh(deliverable),
        });
      }
      return this.refreshTrackers[deliverable.id];
    },
    refreshStateFor(deliverable) {
      return this.refreshStates[deliverable.id] || REFRESH_STATES.IDLE;
    },
    refreshIconFor(deliverable) {
      const state = this.refreshStateFor(deliverable);
      if (state !== REFRESH_STATES.IDLE) return refreshIconClass(state);
      return `icon ${deliverable.reporting_status.icon}`;
    },
    refreshTooltipFor(deliverable) {
      const status = deliverable.reporting_status;
      return refreshTitle(this.refreshStateFor(deliverable), {
        inferredType: deliverable.inferred_type,
        statusMessage: status && status.message,
        statusOk: status ? status.status === "ok" : null,
        updatedLabel: relativeUpdatedLabel(
          deliverable.last_fetched,
          new Date(this.refreshClock)
        ),
      });
    },
    // The "Try refreshing" link is the one place on this screen with room for
    // words rather than a glyph, so it carries the state in plain language.
    refreshLinkLabel(deliverable) {
      switch (this.refreshStateFor(deliverable)) {
        case REFRESH_STATES.REFRESHING:
          return "Refreshing…";
        case REFRESH_STATES.WAITING:
          return "Queued";
        case REFRESH_STATES.DONE:
          return "Updated";
        case REFRESH_STATES.UP_TO_DATE:
          return "Up to date";
        case REFRESH_STATES.UNAVAILABLE:
          return "Can't refresh";
        case REFRESH_STATES.FAILED:
          return "Try again";
        default:
          return "Try refreshing";
      }
    },
    refreshStats(deliverable) {
      const tracker = this.refreshTrackerFor(deliverable);
      this.refreshClock = Date.now();
      tracker.start();

      $.read(`/deliverables/${deliverable.id}/refresh`)
        .done((data) => tracker.enqueued(data && data.status))
        .fail(() => tracker.failed());
    },
    recheckRefresh(deliverable) {
      const before = deliverable.last_fetched;

      $.read(`/deliverables/${deliverable.id}.json`).done((data) => {
        Object.assign(deliverable, data);
        this.refreshClock = Date.now();
        // Only if this row is actually tracking a click of ours —
        // deliverable-updated is a shared event and also fires for uploads and
        // provider webhooks.
        const tracker = this.refreshTrackers[deliverable.id];
        if (tracker && deliverable.last_fetched !== before) tracker.completed();
      });
    },
    // Whether a deliverable counts as "failing" for the Issues modal.
    //
    // This mirrors the server-side `Deliverable.with_reporting_errors` scope,
    // which drives the "N Issues" badge on the assignments list (via
    // Event#deliverables_failing_count — see app/models/deliverable.rb):
    //   with_reporting_errors == with_url.where(
    //     "last_fetched IS NULL OR last_error_at > last_fetched")
    //
    // The previous filter here only checked `!last_fetched`, which matched an
    // OLD version of that scope. Deliverables that fetched successfully once and
    // *later* started erroring (last_error_at > last_fetched) have last_fetched
    // set, so the badge counted them but this modal silently excluded them — the
    // list showed "18 Issues" while the modal showed "0 Issues / No
    // deliverables." (ty 4623)
    //
    // (The badge also chains `without_manually_added_stats`, but the serialized
    // `manual_update_statistics` field is not a reliable client-side signal — it
    // can carry a phantom row for deliverables with no manual stats — so we don't
    // key on it here. Failing deliverables in practice have no manual stats.)
    isFailing(deliverable) {
      if (!deliverable.url || deliverable.skip_refresh) return false;

      // never fetched, OR errored after its last successful fetch
      if (!deliverable.last_fetched) return true;
      return (
        !!deliverable.last_error_at &&
        new Date(deliverable.last_error_at) > new Date(deliverable.last_fetched)
      );
    },
    statusMessage(deliverable) {
      return deliverable.reporting_status.message == ""
        ? "Unknown status"
        : deliverable.reporting_status.message;
    },
    deliverableLabel(deliverable) {
      return !this.hasUrl(deliverable) ? deliverable.name : deliverable.url;
    },
    hasUrl(deliverable) {
      return !isEmpty(deliverable.url);
    },
    openDeliverablesModal() {
      window
        .$(".deliverables.modal")
        .modal({
          allowMultiple: true,
        })
        .modal("show");
    },
    openReportingStatus(deliverable) {
      window.open(`/deliverables/${deliverable.id}/status`, "_blank");
    },
    refreshModal() {
      $(this.$refs.modal).modal("refresh");
    },
    openModal() {
      $(this.$refs.modal)
        .modal({
          observeChanges: true,
        })
        .modal("show");
    },
    showEditLink(deliverable) {
      if (this.hideManageLink) return false;
      if (!deliverable) return false;
      if (window.currentUserId && window.currentUserId == deliverable.user_id) {
        return true;
      } else {
        return false;
      }
    },
    initPopups() {
      $(this.$el)
        .find(".list .item.errored")
        .each(function () {
          $(this)
            .find(".icon")
            .popup({
              closable: false,
              exclusive: true,
              observeChanges: true,
              popup: $(this).find(".popup"),
              hoverable: true,
              distanceAway: 10,
              // position: "left center",
              target: $(this),
              transition: "fade",
              delay: {
                show: 100,
              },
            });
        });
    },
  },
  computed: {
    ...mapState("deliverable", ["all", "colors"]),
    ...mapGetters("deliverable", [
      "inferredTypeAsIconClass",
      "inferredTypeAsLabel",
    ]),

    igStoryDeliverables() {
      return this.all.filter((d) => d.inferred_type === "instagram_stories");
    },
    nonIgStoryDeliverables() {
      return this.all.filter((d) => d.inferred_type != "instagram_stories");
    },
    deliverables() {
      let array = this.nonIgStoryDeliverables;
      if (this.onlyErrored) {
        array = this.erroredDeliverables;
      }
      const sorted = [...array].sort((a, b) => {
        let a_last = new Date(a.last_error_at || a.last_fetched);
        let b_last = new Date(b.last_error_at || b.last_fetched);

        if (!a.last_error_at && !a.last_fetched) return -1;
        if (a.last_fetched && !a.last_error_at) return -1;

        return a_last < b_last ? -1 : 1;
      });
      return sorted;
    },
    erroredDeliverables() {
      return this.nonIgStoryDeliverables.filter((d) => this.isFailing(d));
    },
    neverAttempted() {
      return this.nonIgStoryDeliverables.filter(
        (d) => d.url && !d.last_attempted_at
      );
    },
    disconnectedDeliverables() {
      return this.deliverables.filter(
        (d) =>
          !d.inferred_type.includes("_profile") &&
          d.reporting_status.status == "disconnected"
      );
    },
    allDeliverableErrors() {
      return "I need help troubleshooting deliverables:\n\n" +
        this.deliverables.map(
          (d) => `${d.url} (${d.id}): ${this.statusMessage(d)}`
        ).join("\n");
    },
    requiredProviders() {
      let providers = this.disconnectedDeliverables.map((d) => d.inferred_type);

      providers = providers
        .map((x) => {
          return ["instagram", "instagram_stories"].includes(x)
            ? "facebook"
            : x;
        })
        .map((x) => (x == "url" ? "google" : x));

      providers = new Set(providers);
      providers.delete("other");
      return providers;
    },
  },
  created() {
    // Off the reactive tree on purpose — these own timers.
    this.refreshTrackers = {};
  },
  beforeDestroy() {
    Object.keys(this.refreshTrackers).forEach((id) =>
      this.refreshTrackers[id].destroy()
    );
  },
  mounted() {
    if (this.initialDeliverables) {
      this.$store.dispatch("deliverable/setAll", {
        items: this.initialDeliverables,
      });
      this.refreshModal();

      // The troubleshooter had no completion signal at all, so its refresh
      // could only ever spin. Subscribe to the same stream the report cards
      // use, so a finished refresh lands here too.
      if (typeof window.deliverablesChannel !== "undefined") {
        this.initialDeliverables.forEach((deliverable) => {
          window.deliverablesChannel(
            deliverable.id,
            "deliverable-updated",
            () => this.recheckRefresh(deliverable)
          );
        });
      }
    }
    this.$nextTick(function () {
      this.initPopups();
      let params = new URLSearchParams(document.location.search);

      if (params.get("showIssues") == "true") {
        if (!$(".modal").modal("is active").some(Boolean)) {
          //don't open if another modal is already open on page load
          this.openModal();
        }
      }
    });
  },
  updated() {
    this.$nextTick(function () {
      setTimeout(() => {
        this.refreshModal();
      }, 1000);

      this.initPopups();
    });
  },
};
</script>

<style scoped lang="scss">
.deliverableLabel {
  word-break: break-all;
  overflow: hidden;
  display: inline-flex;
  max-width: 90%;
  text-overflow: ellipsis;
  font-size: 0.8em;
}

.segment .ui.list .item {
  .external {
    visibility: hidden;
  }

  &:hover {
    .external {
      visibility: visible;
    }
  }
}

.ui.top.attached.header {
  text-transform: capitalize;
  color: #fff;
  margin: -1em;
  margin-bottom: 1em;
  border-width: 0;
}
</style>
